import { defineStore } from "pinia";
import { ref, computed } from "vue";

export const useBasketStore = defineStore(
  "basket",
  () => {
    const basket = ref([]);
    const coupon = ref(null);

    // Each basket item carries its own personsCount property — initialised to 1
    // on add. Per Jakub's PR #96 round-3 review: persons selector moved from
    // basket-level (single shared count) to per-term, so a basket with multiple
    // terms can have different counts per term.
    const setProducts = (products) => {
      basket.value = products.map((p) => ({
        ...p,
        personsCount: p.personsCount ?? 1,
      }));
    };

    const removeProduct = (product) => {
      basket.value = basket.value.filter((item) => item.id !== product.id);
    };

    const setTermPersonsCount = (termId, count) => {
      const term = basket.value.find((t) => t.id === termId);
      if (!term) return;
      term.personsCount = Math.max(1, Math.min(count, term.max_persons || 1));
    };

    const totalPrice = computed(() => {
      return priceComputer("price");
    });

    // When the kill switch collapses logged-in pricing, totalLoggedPrice
    // returns the guest amount. Keeps every caller (`loggedIn ? totalLoggedPrice : totalPrice`)
    // working without each having to import the flag.
    const totalLoggedPrice = computed(() => {
      return priceComputer("logged_price");
    });

    const priceComputer = (priceType) => {
      const surchargeType =
        priceType === "price"
          ? "extra_person_price"
          : "extra_person_logged_price";
      const maxCouponUsed = 1;
      let couponUsed = 0;
      return basket.value.reduce((acc, item) => {
        const itemPersons = item.personsCount ?? 1;
        let termPrice =
          item[priceType] +
          (itemPersons - 1) * (item[surchargeType] || 0);
        if (coupon.value && couponUsed < maxCouponUsed) {
          if (coupon.value.percent_discount) {
            couponUsed++;
            termPrice = Math.trunc(termPrice - (termPrice * coupon.value.percent_discount) / 100);
          } else if (coupon.value.amount_discount) {
            couponUsed++;
            termPrice = Math.trunc(Math.max(0, termPrice - coupon.value.amount_discount));
          }
        }
        return acc + termPrice;
      }, 0);
    };

    const clearBasket = () => {
      basket.value = [];
      coupon.value = null;
    };

    const addCoupon = (couponNew) => {
      coupon.value = couponNew;
    };

    const getCoupon = () => {
      return coupon.value;
    };

    const clearCoupon = () => {
      coupon.value = null;
    };

    return {
      basket,
      setProducts,
      removeProduct,
      totalPrice,
      clearBasket,
      totalLoggedPrice,
      addCoupon,
      clearCoupon,
      getCoupon,
      coupon,
      setTermPersonsCount,
    };
  },
  {
    persist: {
      enabled: true,
      afterRestore: (context) => {
        context.store.basket = context.store.basket
          .filter((item) => new Date().getTime() < new Date(item.from).getTime())
          .map((item) => ({
            ...item,
            personsCount: Math.max(
              1,
              Math.min(item.personsCount ?? 1, item.max_persons || 1),
            ),
          }));
      },
    },
  },
);
